import { useState } from 'react';
import { useCategories, PRESET_COLORS, PRESET_ICONS } from '../../../hooks/useCategories';
import type { Category } from '../../../hooks/useCategories';

interface Props {
  open: boolean;
  onClose: () => void;
}

type TabType = 'income' | 'expense';

interface InlineFormState {
  name: string;
  color: string;
  icon: string;
}

const EMPTY_FORM: InlineFormState = { name: '', color: '#22c55e', icon: 'ri-tag-line' };

export const CategoryManager = ({ open, onClose }: Props) => {
  const [tab, setTab] = useState<TabType>('expense');
  const { categories, loading, createCategory, updateCategory, deleteCategory } = useCategories();

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [form, setForm] = useState<InlineFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const filtered = categories.filter((c) => c.type === tab);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM, color: tab === 'income' ? '#22c55e' : '#ef4444' });
    setShowForm(true);
    setFeedback(null);
  };

  const openEdit = (cat: Category) => {
    setEditTarget(cat);
    setForm({ name: cat.name, color: cat.color, icon: cat.icon ?? 'ri-tag-line' });
    setShowForm(true);
    setFeedback(null);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setFeedback(null);
    if (editTarget) {
      const result = await updateCategory(editTarget.id, {
        name: form.name.trim(),
        color: form.color,
        icon: form.icon,
      });
      setFeedback(
        result.success
          ? { type: 'success', message: 'Categoria atualizada.' }
          : { type: 'error', message: 'Erro ao atualizar. Tente novamente.' }
      );
    } else {
      const result = await createCategory({
        name: form.name.trim(),
        type: tab,
        color: form.color,
        icon: form.icon,
      });
      setFeedback(
        result.success
          ? { type: 'success', message: 'Categoria criada com sucesso!' }
          : { type: 'error', message: 'Erro ao criar categoria.' }
      );
    }
    setSaving(false);
    if (!editTarget) { setForm({ ...EMPTY_FORM, color: tab === 'income' ? '#22c55e' : '#ef4444' }); }
  };

  const handleDelete = async (cat: Category) => {
    setDeletingId(cat.id);
    const result = await deleteCategory(cat.id);
    setDeletingId(null);
    setFeedback({ type: result.success ? 'success' : 'error', message: result.message });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md mx-4 max-h-[88vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-700 text-gray-900">Gerenciar Categorias</h3>
            <p className="text-xs text-gray-400 mt-0.5">Categorias padrão não podem ser editadas</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mx-6 mt-4 shrink-0">
          {([['expense', 'Despesas', 'ri-arrow-down-line'], ['income', 'Receitas', 'ri-arrow-up-line']] as const).map(
            ([type, label, icon]) => (
              <button
                key={type}
                onClick={() => { setTab(type); cancelForm(); setFeedback(null); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-600 transition-all cursor-pointer whitespace-nowrap ${
                  tab === type ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${icon} text-sm`} />
                {label}
              </button>
            )
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`mx-6 mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-xl ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            <i className={feedback.type === 'success' ? 'ri-check-circle-line' : 'ri-error-warning-line'} />
            {feedback.message}
          </div>
        )}

        {/* Add button */}
        <div className="px-6 pt-3 pb-1 shrink-0">
          {!showForm ? (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 text-sm font-600 text-emerald-600 hover:text-emerald-700 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-circle-line" />
              Nova categoria de {tab === 'income' ? 'receita' : 'despesa'}
            </button>
          ) : null}
        </div>

        {/* Inline form */}
        {showForm && (
          <div className="mx-6 mb-3 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3 shrink-0">
            <p className="text-xs font-700 text-gray-700 uppercase tracking-wide">
              {editTarget ? 'Editar categoria' : 'Nova categoria'}
            </p>
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Nome"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white"
            />
            <div>
              <p className="text-xs text-gray-500 font-500 mb-1.5">Cor</p>
              <div className="flex gap-2 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, color: c }))}
                    style={{ backgroundColor: c }}
                    className={`w-6 h-6 rounded-full cursor-pointer transition-all ${
                      form.color === c ? 'ring-2 ring-offset-1 ring-gray-500 scale-110' : ''
                    }`}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-500 mb-1.5">Ícone</p>
              <div className="flex gap-1.5 flex-wrap">
                {PRESET_ICONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, icon }))}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer text-sm ${
                      form.icon === icon
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <i className={icon} />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={cancelForm}
                className="flex-1 py-2 text-sm font-600 border border-gray-200 text-gray-500 rounded-xl hover:bg-white cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || saving}
                className="flex-1 py-2 text-sm font-600 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-xs" />}
                {saving ? 'Salvando...' : editTarget ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        )}

        {/* Category list */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="space-y-2 mt-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-11 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">
              Nenhuma categoria {tab === 'income' ? 'de receita' : 'de despesa'} ainda.
            </div>
          ) : (
            <div className="space-y-1.5 mt-1">
              {filtered.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 group"
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
                    style={{ backgroundColor: cat.color + '20' }}
                  >
                    <i className={`${cat.icon ?? 'ri-tag-line'} text-sm`} style={{ color: cat.color }} />
                  </div>
                  <span className="flex-1 text-sm font-500 text-gray-800">{cat.name}</span>
                  {cat.is_default ? (
                    <span className="text-xs text-gray-300 font-500">Padrão</span>
                  ) : (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEdit(cat)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat)}
                        disabled={deletingId === cat.id}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer disabled:opacity-50"
                      >
                        {deletingId === cat.id ? (
                          <i className="ri-loader-4-line animate-spin text-sm" />
                        ) : (
                          <i className="ri-delete-bin-line text-sm" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
